import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { practiceExtrasService } from '../services/practiceExtrasService';
import { patientService } from '../services/patientService';

const ClinicalHub = () => {
  const [tab, setTab] = useState('invoices');
  const [patients, setPatients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [claims, setClaims] = useState([]);
  const [groups, setGroups] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [outcomes, setOutcomes] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const [invForm, setInvForm] = useState({
    patientId: '',
    amountCents: '',
    description: '',
  });
  const [claimForm, setClaimForm] = useState({
    patientId: '',
    dateOfService: '',
    payerName: '',
    cptCodes: '',
    diagnosisCodes: '',
  });
  const [groupForm, setGroupForm] = useState({ title: '', groupType: 'family', memberPatientIds: [] });
  const [waitForm, setWaitForm] = useState({ contactName: '', contactEmail: '', preferredType: 'therapy' });
  const [outcomeForm, setOutcomeForm] = useState({
    patientId: '',
    instrumentName: 'PHQ-9',
    score: '',
    measuredAt: '',
  });

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const [p, inv, cl, gr, wl, out] = await Promise.all([
        patientService.getPatients({}),
        practiceExtrasService.listInvoices(),
        practiceExtrasService.listClaims(),
        practiceExtrasService.listCareGroups(),
        practiceExtrasService.listWaitlist(),
        practiceExtrasService.listOutcomes(),
      ]);
      setPatients(Array.isArray(p) ? p : p.data || []);
      setInvoices(inv.data || []);
      setClaims(cl.data || []);
      setGroups(gr.data || []);
      setWaitlist(wl.data || []);
      setOutcomes(out.data || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const patientLabel = (p) =>
    p?.fullName || [p?.firstName, p?.lastName].filter(Boolean).join(' ') || 'Patient';

  const tabs = [
    { id: 'invoices', label: 'Invoices' },
    { id: 'claims', label: 'Claims' },
    { id: 'outcomes', label: 'Outcomes' },
    { id: 'groups', label: 'Family / Group' },
    { id: 'waitlist', label: 'Waitlist' },
  ];

  return (
    <Layout>
      <div className="p-6 md:p-8">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl font-semibold text-primary mb-2">Clinical Operations</h1>
          <p className="text-gray-500 mb-6">
            Invoices, manual claim tracking, outcomes, family/group care, and waitlist — on MongoDB free tier.
            Clearinghouse auto-submit and eRx remain vendor-gated.
          </p>

          <div className="flex flex-wrap gap-2 mb-6">
            {tabs.map((t) => (
              <button
                key={t.id}
                className={`px-3 py-1.5 rounded-lg text-sm ${
                  tab === t.id ? 'bg-primary text-white' : 'bg-gray-100'
                }`}
                onClick={() => setTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>

          {loading && <p>Loading…</p>}
          {error && <p className="text-red-600">{error}</p>}

          {!loading && tab === 'invoices' && (
            <div className="space-y-4">
              <form
                className="bg-white rounded-xl shadow p-4 grid md:grid-cols-4 gap-3"
                onSubmit={async (e) => {
                  e.preventDefault();
                  await practiceExtrasService.createInvoice({
                    ...invForm,
                    amountCents: Math.round(Number(invForm.amountCents) * 100),
                  });
                  setInvForm({ patientId: '', amountCents: '', description: '' });
                  await load();
                }}
              >
                <select
                  className="border rounded px-2 py-2"
                  value={invForm.patientId}
                  onChange={(e) => setInvForm({ ...invForm, patientId: e.target.value })}
                  required
                >
                  <option value="">Patient</option>
                  {patients.map((p) => (
                    <option key={p._id} value={p._id}>{patientLabel(p)}</option>
                  ))}
                </select>
                <input
                  className="border rounded px-2 py-2"
                  placeholder="Amount (USD)"
                  value={invForm.amountCents}
                  onChange={(e) => setInvForm({ ...invForm, amountCents: e.target.value })}
                  required
                />
                <input
                  className="border rounded px-2 py-2"
                  placeholder="Description"
                  value={invForm.description}
                  onChange={(e) => setInvForm({ ...invForm, description: e.target.value })}
                />
                <button className="bg-primary text-white rounded-lg px-3 py-2">Create invoice</button>
              </form>
              <table className="w-full bg-white rounded-xl shadow text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-3 text-left">#</th>
                    <th className="p-3 text-left">Patient</th>
                    <th className="p-3 text-left">Amount</th>
                    <th className="p-3 text-left">Status</th>
                    <th className="p-3 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv._id} className="border-t">
                      <td className="p-3">{inv.invoiceNumber}</td>
                      <td className="p-3">{patientLabel(inv.patientId)}</td>
                      <td className="p-3">${((inv.amountCents || 0) / 100).toFixed(2)}</td>
                      <td className="p-3">{inv.status}</td>
                      <td className="p-3 space-x-2">
                        <button
                          className="text-primary text-xs underline"
                          onClick={async () => {
                            await practiceExtrasService.updateInvoice(inv._id, { status: 'sent' });
                            await load();
                          }}
                        >
                          Mark sent
                        </button>
                        <button
                          className="text-primary text-xs underline"
                          onClick={async () => {
                            await practiceExtrasService.updateInvoice(inv._id, {
                              status: 'paid',
                              amountPaidCents: inv.amountCents,
                            });
                            await load();
                          }}
                        >
                          Mark paid
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && tab === 'claims' && (
            <div className="space-y-4">
              <form
                className="bg-white rounded-xl shadow p-4 grid md:grid-cols-3 gap-3"
                onSubmit={async (e) => {
                  e.preventDefault();
                  await practiceExtrasService.createClaim(claimForm);
                  setClaimForm({
                    patientId: '',
                    dateOfService: '',
                    payerName: '',
                    cptCodes: '',
                    diagnosisCodes: '',
                  });
                  await load();
                }}
              >
                <select
                  className="border rounded px-2 py-2"
                  value={claimForm.patientId}
                  onChange={(e) => setClaimForm({ ...claimForm, patientId: e.target.value })}
                  required
                >
                  <option value="">Patient</option>
                  {patients.map((p) => (
                    <option key={p._id} value={p._id}>{patientLabel(p)}</option>
                  ))}
                </select>
                <input
                  type="date"
                  className="border rounded px-2 py-2"
                  value={claimForm.dateOfService}
                  onChange={(e) => setClaimForm({ ...claimForm, dateOfService: e.target.value })}
                  required
                />
                <input
                  className="border rounded px-2 py-2"
                  placeholder="Payer"
                  value={claimForm.payerName}
                  onChange={(e) => setClaimForm({ ...claimForm, payerName: e.target.value })}
                />
                <input
                  className="border rounded px-2 py-2"
                  placeholder="CPT"
                  value={claimForm.cptCodes}
                  onChange={(e) => setClaimForm({ ...claimForm, cptCodes: e.target.value })}
                />
                <input
                  className="border rounded px-2 py-2"
                  placeholder="Dx codes"
                  value={claimForm.diagnosisCodes}
                  onChange={(e) => setClaimForm({ ...claimForm, diagnosisCodes: e.target.value })}
                />
                <button className="bg-primary text-white rounded-lg px-3 py-2">Create claim draft</button>
              </form>
              <p className="text-xs text-gray-500">
                Status can be set to submitted_manual after you file outside the app. No auto-clearinghouse.
              </p>
              <table className="w-full bg-white rounded-xl shadow text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-3 text-left">Patient</th>
                    <th className="p-3 text-left">DOS</th>
                    <th className="p-3 text-left">Payer</th>
                    <th className="p-3 text-left">Status</th>
                    <th className="p-3 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {claims.map((c) => (
                    <tr key={c._id} className="border-t">
                      <td className="p-3">{patientLabel(c.patientId)}</td>
                      <td className="p-3">{c.dateOfService ? new Date(c.dateOfService).toLocaleDateString() : '—'}</td>
                      <td className="p-3">{c.payerName || '—'}</td>
                      <td className="p-3">{c.status}</td>
                      <td className="p-3">
                        <button
                          className="text-primary text-xs underline"
                          onClick={async () => {
                            await practiceExtrasService.updateClaim(c._id, {
                              status: 'submitted_manual',
                            });
                            await load();
                          }}
                        >
                          Mark submitted (manual)
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && tab === 'outcomes' && (
            <div className="space-y-4">
              <form
                className="bg-white rounded-xl shadow p-4 grid md:grid-cols-5 gap-3"
                onSubmit={async (e) => {
                  e.preventDefault();
                  await practiceExtrasService.addOutcome({
                    ...outcomeForm,
                    score: Number(outcomeForm.score),
                    measuredAt: outcomeForm.measuredAt || new Date().toISOString(),
                  });
                  setOutcomeForm({ patientId: '', instrumentName: 'PHQ-9', score: '', measuredAt: '' });
                  await load();
                }}
              >
                <select
                  className="border rounded px-2 py-2"
                  value={outcomeForm.patientId}
                  onChange={(e) => setOutcomeForm({ ...outcomeForm, patientId: e.target.value })}
                  required
                >
                  <option value="">Patient</option>
                  {patients.map((p) => (
                    <option key={p._id} value={p._id}>{patientLabel(p)}</option>
                  ))}
                </select>
                <input
                  className="border rounded px-2 py-2"
                  value={outcomeForm.instrumentName}
                  onChange={(e) => setOutcomeForm({ ...outcomeForm, instrumentName: e.target.value })}
                  placeholder="Instrument"
                />
                <input
                  className="border rounded px-2 py-2"
                  type="number"
                  value={outcomeForm.score}
                  onChange={(e) => setOutcomeForm({ ...outcomeForm, score: e.target.value })}
                  placeholder="Score"
                  required
                />
                <input
                  className="border rounded px-2 py-2"
                  type="date"
                  value={outcomeForm.measuredAt}
                  onChange={(e) => setOutcomeForm({ ...outcomeForm, measuredAt: e.target.value })}
                />
                <button className="bg-primary text-white rounded-lg px-3 py-2">Add score</button>
              </form>
              <table className="w-full bg-white rounded-xl shadow text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-3 text-left">Patient</th>
                    <th className="p-3 text-left">Instrument</th>
                    <th className="p-3 text-left">Score</th>
                    <th className="p-3 text-left">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {outcomes.map((o) => (
                    <tr key={o._id} className="border-t">
                      <td className="p-3">{patientLabel(o.patientId)}</td>
                      <td className="p-3">{o.instrumentName}</td>
                      <td className="p-3">{o.score}</td>
                      <td className="p-3">{o.measuredAt ? new Date(o.measuredAt).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && tab === 'groups' && (
            <div className="space-y-4">
              <form
                className="bg-white rounded-xl shadow p-4 grid md:grid-cols-3 gap-3"
                onSubmit={async (e) => {
                  e.preventDefault();
                  await practiceExtrasService.createCareGroup(groupForm);
                  setGroupForm({ title: '', groupType: 'family', memberPatientIds: [] });
                  await load();
                }}
              >
                <input
                  className="border rounded px-2 py-2"
                  placeholder="Group title"
                  value={groupForm.title}
                  onChange={(e) => setGroupForm({ ...groupForm, title: e.target.value })}
                  required
                />
                <select
                  className="border rounded px-2 py-2"
                  value={groupForm.groupType}
                  onChange={(e) => setGroupForm({ ...groupForm, groupType: e.target.value })}
                >
                  <option value="family">Family</option>
                  <option value="couple">Couple</option>
                  <option value="group_therapy">Group therapy</option>
                </select>
                <select
                  multiple
                  className="border rounded px-2 py-2 min-h-[80px]"
                  value={groupForm.memberPatientIds}
                  onChange={(e) =>
                    setGroupForm({
                      ...groupForm,
                      memberPatientIds: Array.from(e.target.selectedOptions).map((o) => o.value),
                    })
                  }
                >
                  {patients.map((p) => (
                    <option key={p._id} value={p._id}>{patientLabel(p)}</option>
                  ))}
                </select>
                <button className="bg-primary text-white rounded-lg px-3 py-2 md:col-span-3">
                  Create care group
                </button>
              </form>
              <ul className="bg-white rounded-xl shadow divide-y">
                {groups.map((g) => (
                  <li key={g._id} className="p-4 text-sm">
                    <div className="font-medium">{g.title} · {g.groupType}</div>
                    <div className="text-gray-500">
                      Members:{' '}
                      {(g.memberPatientIds || []).map((m) => patientLabel(m)).join(', ') || '—'}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!loading && tab === 'waitlist' && (
            <div className="space-y-4">
              <form
                className="bg-white rounded-xl shadow p-4 grid md:grid-cols-4 gap-3"
                onSubmit={async (e) => {
                  e.preventDefault();
                  await practiceExtrasService.addWaitlist(waitForm);
                  setWaitForm({ contactName: '', contactEmail: '', preferredType: 'therapy' });
                  await load();
                }}
              >
                <input
                  className="border rounded px-2 py-2"
                  placeholder="Contact name"
                  value={waitForm.contactName}
                  onChange={(e) => setWaitForm({ ...waitForm, contactName: e.target.value })}
                  required
                />
                <input
                  className="border rounded px-2 py-2"
                  placeholder="Email"
                  value={waitForm.contactEmail}
                  onChange={(e) => setWaitForm({ ...waitForm, contactEmail: e.target.value })}
                />
                <select
                  className="border rounded px-2 py-2"
                  value={waitForm.preferredType}
                  onChange={(e) => setWaitForm({ ...waitForm, preferredType: e.target.value })}
                >
                  <option value="therapy">Therapy</option>
                  <option value="assessment">Assessment</option>
                  <option value="intake">Intake</option>
                </select>
                <button className="bg-primary text-white rounded-lg px-3 py-2">Add to waitlist</button>
              </form>
              <ul className="bg-white rounded-xl shadow divide-y">
                {waitlist.map((w) => (
                  <li key={w._id} className="p-4 text-sm flex justify-between gap-2">
                    <div>
                      <div className="font-medium">{w.contactName}</div>
                      <div className="text-gray-500">
                        {w.preferredType} · {w.status} · {w.contactEmail}
                      </div>
                    </div>
                    {w.status === 'waiting' && (
                      <button
                        className="text-primary text-xs underline"
                        onClick={async () => {
                          await practiceExtrasService.updateWaitlist(w._id, { status: 'offered' });
                          await load();
                        }}
                      >
                        Mark offered
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ClinicalHub;
